import { ApolloError } from "@apollo/client";
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import React from "react";
import {
  DataTableContainer,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
} from "../../DataTableContainer";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import PayeeTableToolbar from "../PayeeTableToolbar";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { PAGE_SIZE } from "./useTransactionsUrlState";

export interface TransactionsGridProps {
  tableName: string;
  defaultVisibilityModel: GridColumnVisibilityModel;
  columns: GridColDef[];
  exportColumns: GridColDef[];
  exportFileName: string;
  rows: CurbsuiteTransactionLogEntry[];
  rowCount: number;
  loading: boolean;
  error: ApolloError | undefined;
  page: number;
  onPageChange: (newPage: number) => void;
  sortModel: GridSortModel;
  onSortModelChange: (newSortModel: GridSortModel) => void;
  filterModel: GridFilterModel | undefined;
  onFilterModelChange: (newFilterModel: GridFilterModel) => void;
  onApplyFilters: (newFilterModel: GridFilterModel) => void;
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<CurbsuiteTransactionLogEntry[]>;
  selectedDate: Date;
  onChangeSelectedDate: (newDate: Date) => void;
  maxDate: Date;
}

/**
 * The DataGridPro/DataTableContainer render shell -- identical across every shape (regular, bus
 * station, airport, park or wallet). Everything shape-specific (columns, data, URL state) is
 * assembled by the caller; this component just wires it into the grid.
 */
export const TransactionsGrid: React.FC<TransactionsGridProps> = ({
  tableName,
  defaultVisibilityModel,
  columns,
  exportColumns,
  exportFileName,
  rows,
  rowCount,
  loading,
  error,
  page,
  onPageChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  onApplyFilters,
  fetchTotalCount,
  fetchDataBatch,
  selectedDate,
  onChangeSelectedDate,
  maxDate,
}) => (
  <DataTableContainer tableName={tableName} defaultVisibilityModel={defaultVisibilityModel}>
    {({ apiRef, initialState, isIconOnly, resetLayout }) => (
      <DataGridPro
        apiRef={apiRef}
        localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
        components={{
          Toolbar: PayeeTableToolbar,
          FilterPanel: ManualApplyGridFilterPanel,
          ColumnsPanel: FilteredColumnsPanel,
        }}
        componentsProps={{
          filterPanel: {
            onApplyFilters,
          },
          toolbar: {
            fetchTotalCount,
            fetchDataBatch,
            exportFileName,
            columns: exportColumns,
            selectedDate,
            onChangeSelectedDate,
            maxDate,
            batchSize: PAGE_SIZE,
            defaultVisibilityModel,
            isIconOnly,
            resetLayout,
          },
        }}
        getRowId={(row) => row.transactionId}
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        {...STANDARD_GRID_PROPS}
        error={error}
        pagination
        page={page - 1}
        onPageChange={onPageChange}
        pageSize={PAGE_SIZE}
        paginationMode={"server"}
        rowsPerPageOptions={[PAGE_SIZE]}
        sortingMode={"server"}
        sortModel={sortModel}
        filterMode={"server"}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        initialState={initialState}
        onSortModelChange={onSortModelChange}
        sx={{
          flexGrow: 1,
          ...STANDARD_COLUMN_HEADER_TITLE_SX,
        }}
      />
    )}
  </DataTableContainer>
);
